import { useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useColorScheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DatasetIcon from "@mui/icons-material/DatasetOutlined";

import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState, ErrorState, LoadingState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { StatusChip } from "@/components/display/status-chip";
import { MetricCard } from "@/components/display/metric-card";
import { IdChip, MonoText } from "@/components/display/mono";
import { RelativeTime } from "@/components/display/relative-time";
import { LiveDot } from "@/components/display/live-dot";
import { CodeBlock } from "@/components/display/code-block";
import { JsonViewer } from "@/components/display/json-viewer";
import { WorkspaceTag } from "@/components/display/workspace-tag";
import { Form } from "@/components/form/form";
import { FormSelect, FormSwitch, FormTextField } from "@/components/form/fields";

export const Route = createFileRoute("/dev/showcase")({
  // Component gallery — dev only. Returns 404 in a production build.
  beforeLoad: () => {
    if (import.meta.env.PROD) throw notFound();
  },
  component: Showcase,
});

const STATUSES = ["SUCCESS", "RUNNING", "FAILED", "STALE", "QUEUED", "DRAFT", "PARTIAL"];

interface DemoRow {
  id: string;
  name: string;
  status: string;
  records: number;
  ranAt: string;
}
const DEMO_ROWS: DemoRow[] = [
  {
    id: "pl_a1b2",
    name: "vendor-orders-nightly",
    status: "SUCCESS",
    records: 18234,
    ranAt: "2026-09-05T02:10:00Z",
  },
  {
    id: "pl_c3d4",
    name: "fraud-events-stream",
    status: "RUNNING",
    records: 902,
    ranAt: "2026-09-05T08:41:00Z",
  },
  {
    id: "pl_e5f6",
    name: "claims-reconcile",
    status: "FAILED",
    records: 0,
    ranAt: "2026-09-04T23:55:00Z",
  },
];
const COLUMNS: Column<DemoRow>[] = [
  { id: "name", header: "Pipeline", cell: (r) => r.name, sortValue: (r) => r.name },
  { id: "id", header: "ID", cell: (r) => <IdChip value={r.id} /> },
  { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
  {
    id: "records",
    header: "Records",
    align: "right",
    cell: (r) => r.records.toLocaleString(),
    sortValue: (r) => r.records,
  },
  {
    id: "ranAt",
    header: "Ran",
    cell: (r) => <RelativeTime value={r.ranAt} />,
    sortValue: (r) => r.ranAt,
  },
];

const demoSchema = z.object({
  name: z.string().min(3, "At least 3 characters"),
  mode: z.enum(["BATCH", "REALTIME"]),
  enabled: z.boolean(),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Showcase() {
  const { mode, setMode } = useColorScheme();
  const toast = useToast();
  const confirm = useConfirm();
  const [tableState, setTableState] = useState<"data" | "loading" | "empty" | "error">("data");

  const form = useForm<z.infer<typeof demoSchema>>({
    resolver: zodResolver(demoSchema),
    defaultValues: { name: "", mode: "BATCH", enabled: true },
  });

  return (
    <PageContainer>
      <PageHeader
        title="Design system"
        description="Every primitive, every state. Toggle the theme to check both colour schemes."
        actions={
          <Button variant="outlined" onClick={() => setMode(mode === "dark" ? "light" : "dark")}>
            {mode === "dark" ? "Light" : "Dark"} mode
          </Button>
        }
      />

      <Section title="Typography">
        <Stack spacing={0.5}>
          <Typography variant="h1">Heading 1</Typography>
          <Typography variant="h2">Heading 2</Typography>
          <Typography variant="h3">Heading 3</Typography>
          <Typography variant="body1">
            Body 1 — the quick brown fox jumps over the lazy dog.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Body 2 secondary — supporting copy.
          </Typography>
          <MonoText>mono: jdbc:postgresql://db:5432/udip</MonoText>
        </Stack>
      </Section>

      <Section title="Buttons">
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button variant="contained">Primary</Button>
          <Button variant="outlined">Outlined</Button>
          <Button variant="text">Text</Button>
          <Button variant="contained" color="error">
            Destructive
          </Button>
          <Button variant="contained" disabled>
            Disabled
          </Button>
        </Stack>
      </Section>

      <Section title="Status chips">
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {STATUSES.map((s) => (
            <StatusChip key={s} status={s} />
          ))}
          <StatusChip status="WEIRD_UNKNOWN_STATE" />
        </Stack>
      </Section>

      <Section title="Metric cards">
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          }}
        >
          <MetricCard
            label="Runs today"
            value="1,204"
            hint="+8% vs yesterday"
            icon={<DatasetIcon />}
          />
          <MetricCard label="Success rate" value="98.2%" tone="success" />
          <MetricCard label="Failures" value="21" tone="error" />
          <MetricCard label="Avg duration" value="—" loading />
        </Box>
      </Section>

      <Section title="Data table">
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {(["data", "loading", "empty", "error"] as const).map((s) => (
            <Button
              key={s}
              size="small"
              variant={tableState === s ? "contained" : "outlined"}
              onClick={() => setTableState(s)}
            >
              {s}
            </Button>
          ))}
        </Stack>
        <Card>
          <DataTable
            columns={COLUMNS}
            data={tableState === "empty" ? [] : tableState === "loading" ? undefined : DEMO_ROWS}
            isLoading={tableState === "loading"}
            error={tableState === "error" ? new Error("Upstream returned 502") : undefined}
            onRetry={() => setTableState("data")}
            rowKey={(r) => r.id}
            onRowClick={(r) => toast.info(`Row: ${r.name}`)}
            empty={
              <EmptyState title="No runs yet" description="Trigger a pipeline to see runs here." />
            }
          />
        </Card>
      </Section>

      <Section title="Feedback states (standalone)">
        <Box
          sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}
        >
          <Card>
            <LoadingState />
          </Card>
          <Card>
            <EmptyState title="Nothing here" description="Create your first connector." />
          </Card>
          <Card>
            <ErrorState
              error={new Error("Network unreachable")}
              onRetry={() => toast.info("retry")}
            />
          </Card>
        </Box>
      </Section>

      <Section title="Toasts & confirm">
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={() => toast.success("Saved")}>
            Success toast
          </Button>
          <Button variant="outlined" onClick={() => toast.error("Something failed")}>
            Error toast
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={async () => {
              const ok = await confirm({
                title: "Delete pipeline?",
                description: "This removes the pipeline and its run history.",
                destructive: true,
                confirmLabel: "Delete",
              });
              toast.info(ok ? "Confirmed" : "Cancelled");
            }}
          >
            Confirm dialog
          </Button>
        </Stack>
      </Section>

      <Section title="Form fields (react-hook-form + zod)">
        <Card sx={{ p: 3, maxWidth: 480 }}>
          <Form form={form} onSubmit={(v) => toast.success(JSON.stringify(v))}>
            <Stack spacing={2}>
              <FormTextField control={form.control} name="name" label="Pipeline name" required />
              <FormSelect
                control={form.control}
                name="mode"
                label="Execution mode"
                options={[
                  { value: "BATCH", label: "Batch" },
                  { value: "REALTIME", label: "Realtime" },
                ]}
              />
              <FormSwitch control={form.control} name="enabled" label="Enabled" />
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </Stack>
          </Form>
        </Card>
      </Section>

      <Section title="Display bits">
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <LiveDot status="online" label="Connected" />
            <LiveDot status="degraded" label="Degraded" />
            <LiveDot status="offline" label="Offline" />
            <LiveDot status="connecting" label="Connecting" />
            <WorkspaceTag name="finance" color="#0ea5e9" />
            <WorkspaceTag name="default" />
            <IdChip value="exec_9f2a1c8b7d6e5f4a" truncate={12} />
          </Stack>
          <Divider />
          <CodeBlock
            code={'curl -H "X-API-Key: udip_..." https://udip/api/v1/external/whoami'}
            language="bash"
          />
          <JsonViewer
            value={{ id: "pl_a1b2", steps: ["FIELD_MAP", "FILTER"], config: { slaSeconds: 300 } }}
          />
        </Stack>
      </Section>
    </PageContainer>
  );
}
